// File: src/modules/jobs/JobForm.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Modal from '../../components/shared/Modal';
import Checkbox from '../../components/shared/Checkbox';
import { createJob, fetchAcceptedEstimates, updateJobStatus, fetchJobById, updateJob } from './jobsService';
import { getTimeTrackingPrefs } from '../time-tracking/timeTrackingService';
import EstimateAutocomplete from './EstimateAutocomplete';
import { Container, Button, Title } from './JobStyles';
import { FaArrowRight, FaCalendarAlt, FaEnvelope, FaPhone, FaUserAlt } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css'; // Import the CSS for styling
import './customStyle.css';
import ScheduledJobs from './ScheduledJobs';
import DashboardLayout from '../../layouts/DashboardLayout';
import { discountInfo } from '../../utils/discount';
import { formatCurrency } from '../../utils/utils';

const FormWrapper = styled.div`
  max-width: 800px;
  margin: auto;
  padding: ${(props) => props.theme.spacing(4)};
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const TotalPrice = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1.1em;
  font-weight: bold;
  padding-top: 10px;
`;

const SummaryWrapper = styled.div`
  margin-top: ${(props) => props.theme.spacing(3)};
  padding: 2em;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const SummaryTitle = styled.h3`
  font-size: 20px;
  color: ${(props) => props.theme.colors.primaryText};
  padding: 0px 0em 1em 0px;
  svg{
  margin-right: 10px;}
`;

const SummaryList = styled.ul`
  list-style: none;
  padding: 0;
`;

const SummaryItem = styled.li`
  padding: ${(props) => props.theme.spacing(2)} 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.inputGray};
  display: flex;
  justify-content: space-between;
  svg{margin-right: 10px;}
  &:last-child {
    border-bottom: none;
  }
`;

const CardWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing(3)};
  margin-top: ${(props) => props.theme.spacing(1)};
  margin-bottom: ${(props) => props.theme.spacing(2)};
  
`;

const Card = styled.div`
  flex: 1 1 calc(33.33% - ${(props) => props.theme.spacing(3)});
  padding: ${(props) => props.theme.spacing(3)};
  background-color: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  svg{
    margin-right: 10px;
  }
  @media (max-width: 768px) {
    flex: 1 1 100%;
  }`;

const DateInputLabel = styled.label`
  font-size: 1.2em;
  color: ${(props) => props.theme.colors.primaryText};
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  svg {
    margin-right: 8px;
  }
`;

const JobForm = () => {
  const [jobData, setJobData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    job_address: '',
    job_city: '',
    job_state: '',
    job_zip_code: '',
    job_country: 'USA',
    scheduled_start_date: '',
    scheduled_end_date: '',
    status: 'Scheduled',
    total_price: '',
    subtotal: '',
    discount: ''
  });

  const [addressLatLng, setAddressLatLng] = useState({ lat: null, lng: null });
  const [hasDiscount, setHasDiscount] = useState(false);
  const [sameAsEstimate, setSameAsEstimate] = useState(true);
  // "Add this job as a clock-in site" (defaults come from Time Tracking settings).
  const [addAsSite, setAddAsSite] = useState(false);
  const [siteRadius, setSiteRadius] = useState(600);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState({});
  const [estimates, setEstimates] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const history = useNavigate();
  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [scheduledStartDate, setScheduledStartDate] = useState(new Date());
  const [scheduledEndDate, setScheduledEndDate] = useState(null);
  const { job_id } = useParams();
  const [isEstimateSelected, setIsEstimateSelected] = useState(false);
  const [withEstimate, setWithEstimate] = useState(null);
  const {estimate_id} = useParams();

  
  // Fetch accepted estimates for the autocomplete input
  useEffect(() => {
    const fetchEstimates = async () => {
      try {
        const data = await fetchAcceptedEstimates(); // Function to get accepted estimates
        setEstimates(data);
      } catch (error) {
        console.error('Error fetching estimates:', error);
      }
    };

    fetchEstimates();
  }, []);

  // For NEW jobs, seed the "add as clock-in site" defaults from Time Tracking settings.
  useEffect(() => {
    if (job_id) return;
    getTimeTrackingPrefs()
      .then((p) => { setAddAsSite(!!p.auto_create_site); setSiteRadius(p.default_radius_feet); })
      .catch(() => {});
  }, [job_id]);

  useEffect(() => {
    console.log(" Porra" );
    if (job_id) {
      setIsEditMode(true);
      setJobId(job_id);
      fetchJobData(job_id);
    }
    if(estimate_id){
      setWithEstimate(true);
    }
  }, [location]);

  useEffect(() => {
    const runAsyncTask = async () => {
      
      if (isEditMode && estimates.length > 0 && jobData.estimate_id && !isEstimateSelected) {
        const matchingEstimate = estimates.find(
          (estimate) => estimate.id === jobData.estimate_id
        );        
        if (matchingEstimate) {
          await handleEstimateSelect(matchingEstimate);
          setIsEstimateSelected(true); // No need for await here, since setState is synchronous
        }
      }
      if(withEstimate && estimates.length > 0 && estimate_id && !isEstimateSelected){
        const matchingEstimate = estimates.find(
          (estimate) => estimate.id.toString() === estimate_id.toString()
        );
        if(matchingEstimate){
          await handleEstimateSelect(matchingEstimate);
          setIsEstimateSelected(true);
        }
      }
    };
  
    runAsyncTask();
  }, [isEditMode, estimates, jobData, isEstimateSelected, withEstimate]);


  const fetchJobData = async (id) => {
    try {
      const jobData = await fetchJobById(id);
      setJobData(jobData);
      setSelectedEstimate(jobData.estimate);
      setScheduledStartDate(new Date(jobData.scheduled_start_date));
      setScheduledEndDate(new Date(jobData.scheduled_end_date));
      setAddressLatLng({ lat: jobData.latitude, lng: jobData.longitude });
    } catch (error) {
      console.error('Error fetching job data:', error);
      setError({ ...error, fetchError: 'Failed to fetch job data' });
    }
  };

  const handleResetForm = () => {
    setJobData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      job_address: '',
      job_city: '',
      job_state: '',
      job_zip_code: '',
      job_country: 'USA',
      scheduled_start_date: '',
      scheduled_end_date: '',
      status: 'Scheduled',
      total_price: '',
      subtotal: '',
      discount: ''
    });
    setSelectedEstimate(null);
    setError({});
    return;
  }

  const handleEstimateChange = (value) => {
    if(!value) handleResetForm();
    return;
  }

  // Handle estimate selection and populate form fields
  const handleEstimateSelect = (estimate) => {
    if (!estimate) { handleResetForm();}

    const { name, email, phone } = estimate.Lead;
    const addrSource = estimate.leadAddress || estimate.Lead;
    const { street, city, state, zipcode, latitude, longitude } = addrSource;
    const {id, leadId, totalPrice, subtotal, discountPercentage, discountReason} = estimate || {};
    const geolocation = latitude + ',' + longitude;
  
    setError({ customerName: '' });

    setSelectedEstimate(estimate);
   
    // Split geolocation into latitude and longitude
    const [lat, lng] = geolocation ? geolocation.split(',') : [null, null];
    
    if(discountPercentage) setHasDiscount(true);
    else setHasDiscount(false);

    // Populate customer and job site data with estimate info
    setJobData({
      ...jobData,
      customer_name: name || '',
      customer_email: email || '',
      customer_phone: phone || '',
      job_address: street || '',
      job_city: city || '',
      job_state: state || '',
      job_zip_code: zipcode || '',
      total_price: totalPrice || '',
      subtotal: totalPrice || '',
      discount: discountPercentage || '',
      discountReason,
      lead_id: leadId || '',
      estimate_id: id || '',
    });

    // Automatically set the latitude and longitude for the job site
    setAddressLatLng({ lat: parseFloat(lat), lng: parseFloat(lng) });
  };

   // Handle start date change
   const handleStartDateChange = (date) => {
    if(!date || date < new Date('m/d/y')){
      setError({...error, scheduledStartDate: 'The start date must be today or later'});
    }else{
      setScheduledStartDate(date);
      setScheduledEndDate(date); // Reset end date when start date changes
      setJobData({ ...jobData, scheduled_start_date: date.toISOString(), scheduled_end_date: date.toISOString() });
      setError({...error, scheduledStartDate: ''});
    }
  };

  // Handle end date change
  const handleEndDateChange = (date) => {
    if (date >= scheduledStartDate) { // Ensure end date is after start date
      setScheduledEndDate(date);
      setJobData({ ...jobData, scheduled_end_date: date.toISOString() });
      setError({ ...error, scheduledEndDate: '' });
    }else{
      setError({ ...error, scheduledEndDate: 'End date must be after start date' });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {

    e.preventDefault();
    const newError = {};

    if (!jobData.scheduled_start_date) {
      newError.scheduledStartDate = 'Start date is required';
    }
    
    if (!jobData.scheduled_end_date) {
      newError.scheduledEndDate = 'End date is required';
    }

    if(!jobData.customer_name || !selectedEstimate){
      newError.customerName = 'Select the Estimate to schedule the Job';
    }

    if (Object.keys(newError).length > 0) {
      setError(newError); // Set the error state to show messages
      return;
    }
    // Show confirmation modal before creating the job
    setShowConfirmation(true);
  };

  // Handle confirmation of job creation
  const handleConfirmSubmit = async () => {
    console.log()
    try {
      if (isEditMode) {
        console.log(jobData.scheduled_start_date);
        console.log(jobData.scheduled_end_date);

        const response = await updateJob(jobId, {
          ...jobData,
          scheduled_start_date: jobData.scheduled_start_date,
          scheduled_end_date: jobData.scheduled_end_date,
          discount: jobData.discount || 0,
        });
        console.log("Editando: ", response);
        history('/jobs');
      } else {
        await createJob({
          ...jobData,
          latitude: addressLatLng.lat,
          longitude: addressLatLng.lng,
          estimateId: selectedEstimate?.estimate_id || null,
          status: 'Scheduled',
          services: selectedEstimate.items,
          // Optionally also register this location as a clock-in geofence.
          add_as_site: addAsSite,
          site_name: addAsSite ? (jobData.job_address || jobData.customer_name || 'Job site') : undefined,
          site_radius_feet: addAsSite ? (Number(siteRadius) || 600) : undefined,
        });
        history('/jobs');
      }
    } catch (error) {
      console.error('Error creating job:', error);
    } finally {
      setShowConfirmation(false); // Hide modal after confirmation
    }
  };

  return (
    <DashboardLayout>
    <Container>
     <Title>{isEditMode ? 'Edit Job' : 'Create New Job'}</Title>
      <FormWrapper>
        <form onSubmit={handleSubmit}>
          {/* Estimate Selection Autocomplete */}
          <EstimateAutocomplete
            label="Search Estimate"
            value={selectedEstimate}
            suggestions={estimates}
            onSelect={handleEstimateSelect} 
            placeholder="Select an accepted estimate"
            onChange={handleEstimateChange}
            disabled={isEditMode}
          />
          {error.customerName && (
            <div className="error-message">
              {error.customerName}
            </div>
          )}

          {jobData && jobData.customer_name && jobData.customer_name.length > 0 && (
          <SummaryWrapper>
            <SummaryTitle><FaUserAlt /> {jobData.customer_name}</SummaryTitle>
            <SummaryList>
              <SummaryItem><span><FaEnvelope /> {jobData.customer_email}</span><span><FaPhone />{jobData.customer_phone}</span></SummaryItem>
              <SummaryItem><span>{jobData.job_address}</span></SummaryItem>
            </SummaryList>
          </SummaryWrapper>
          )}

          {/* Summary of Services */}
          {selectedEstimate && selectedEstimate.items && selectedEstimate.items.length > 0 && (
            <SummaryWrapper>
              <SummaryTitle>Services Summary</SummaryTitle>
              <SummaryList>
                {selectedEstimate.items.map((service, index) => (
                  <SummaryItem key={index}>
                    <span>{service.Service.service_name} - {service.totalArea} sqft</span>
                    <span>${service.totalPrice}</span>
                  </SummaryItem>
                ))}
                <SummaryList>
                  {(() => {
                    const discount = discountInfo(selectedEstimate, selectedEstimate.originalSubtotal ?? selectedEstimate.subtotal);
                    if (!discount.hasDiscount) return null;
                    return (
                      <SummaryItem>
                        <span>{discount.label}{discount.reason ? ` - ${discount.reason}` : ''}</span>
                        <span>-{formatCurrency(discount.amount)}</span>
                      </SummaryItem>
                    );
                  })()}
                </SummaryList>
                <TotalPrice>
                  <span>Total Price:</span> 
                  <span>${selectedEstimate.totalPrice}</span>
                </TotalPrice>
              </SummaryList>
            </SummaryWrapper>
          )}

          {selectedEstimate && selectedEstimate.details && selectedEstimate.details.length > 0 && (
          <CardWrapper>
            {selectedEstimate.details.map((detail, index) => (
              <Card key={index}><FaArrowRight />  {detail.name}</Card>
            ))}
          </CardWrapper>
          )}

          {/* Date Selection */}
          <CardWrapper>
            <Card>
              <DateInputLabel><FaCalendarAlt /> Start Date</DateInputLabel>
              <DatePicker 
                selected={scheduledStartDate}
                onChange={handleStartDateChange}
                minDate={new Date()} // Disable dates before today
                dateFormat="MM/dd/yyyy"
              />
              {error.scheduledStartDate && (
                <div className="error-message">
                  {error.scheduledStartDate}
                </div>
              )}
            </Card>
            <Card>
              <DateInputLabel><FaCalendarAlt /> End Date</DateInputLabel>
              <DatePicker
                selected={scheduledEndDate}
                onChange={handleEndDateChange}
                minDate={scheduledStartDate} // Ensure end date is after start date
                dateFormat="MM/dd/yyyy"
              />
              {error.scheduledEndDate && (
                <div className="error-message">
                  {error.scheduledEndDate}
                </div>
              )}
            </Card>
          </CardWrapper>
          {!isEditMode && (
            <CardWrapper>
              <Card>
                <Checkbox
                  label="Add this job location as a clock-in site (employees can clock in here)"
                  checked={addAsSite}
                  onChange={(e) => setAddAsSite(e.target.checked)}
                />
                {addAsSite && (
                  <div style={{ marginTop: 8, maxWidth: 260 }}>
                    <label style={{ display: 'block', fontSize: 14, color: '#CCCCCC', marginBottom: 4 }}>
                      Clock-in radius (feet)
                    </label>
                    <input
                      type="number" min="1" value={siteRadius}
                      onChange={(e) => setSiteRadius(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 12px', background: '#272727', color: '#fff',
                        border: '1px solid #787878', borderRadius: 8,
                      }}
                    />
                  </div>
                )}
              </Card>
            </CardWrapper>
          )}

          <CardWrapper>
            <Button onClick={handleSubmit} type="submit">{isEditMode ? 'Update Job' : 'Create Job'}</Button>
          </CardWrapper>

          {selectedEstimate && selectedEstimate.details && selectedEstimate.details.length > 0 && (
          <CardWrapper>
            <Card>
              {/* existing components */}
              <ScheduledJobs currentJobLatLng={addressLatLng} /> {/* Pass the center coordinates */}
            </Card>
          </CardWrapper>
          )}

        </form>
      </FormWrapper>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <Modal
          title={isEditMode ? "Confirm Job Update" : "Confirm Job Creation"}
          message={isEditMode ? "Are you sure you want to update this job?" : "Are you sure you want to create this job?"}
          onConfirm={handleConfirmSubmit}
          onCancel={() => setShowConfirmation(false)}
          isOpen={isEditMode || showConfirmation}
        />
      )}
    </Container>
    </DashboardLayout>
  );
};

export default JobForm;
